import axios from 'axios';

// Get backend URL from environment variable
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Log the backend URL for debugging (only in development)
if (process.env.NODE_ENV === 'development') {
  console.log('Backend URL:', BACKEND_URL);
}

// Ensure we have a valid URL
if (!BACKEND_URL && process.env.NODE_ENV === 'production') {
  console.error('CRITICAL: REACT_APP_BACKEND_URL is not set in production!');
}

export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  timeout: 60000, // 60 second timeout (Render free tier can be slow to wake up)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

// Sleep function for retry delay
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add retry count to config if not present
    if (config._retryCount === undefined) {
      config._retryCount = 0;
    }
    
    return config;
  },
  (error) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors with retry logic
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const config = error.config;
    
    // Log error details for debugging
    console.error('API Error:', {
      url: config?.url,
      method: config?.method,
      status: error.response?.status,
      message: error.message,
      data: error.response?.data,
    });

    // Retry logic for network errors or 5xx server errors
    const shouldRetry = (
      config &&
      config._retryCount < MAX_RETRIES &&
      (
        !error.response || // Network error
        error.response.status >= 500 || // Server error
        error.response.status === 408 // Timeout
      )
    );

    if (shouldRetry) {
      config._retryCount += 1;
      console.log(`Retrying request (${config._retryCount}/${MAX_RETRIES}):`, config.url);
      
      // Wait before retrying
      await sleep(RETRY_DELAY * config._retryCount);
      
      return api(config);
    }

    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_data');
      if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;
